// Client Projects Workspace — SortControls Component
// Requirements: 4.1, 4.3, 4.5, 9.3
// Refactored to use DashboardFilterChip for visual consistency with other dashboard sections.

import type { ClientSortOption } from '../../../../types/client-workspace';
import { DashboardFilterChip } from '../../components/primitives/DashboardFilterChip';
import { safeStorageGet, safeStorageSet } from '../utils/storage';

const SORT_STORAGE_KEY = 'client-workspace-sort';

interface SortOption {
  value: string;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { value: 'recent_activity', label: 'Most Recent Activity' },
  { value: 'last_updated', label: 'Last Updated' },
  { value: 'due_date', label: 'Due Date' },
  { value: 'alphabetical', label: 'Alphabetical' },
  { value: 'recently_delivered', label: 'Recently Delivered' },
  { value: 'priority', label: 'Priority' },
  { value: 'approval_needed', label: 'Approval Needed' },
];

const VALID_SORT_VALUES = new Set<string>(SORT_OPTIONS.map((o) => o.value));

const DEFAULT_SORT: ClientSortOption = 'recent_activity';

interface SortControlsProps {
  value: ClientSortOption;
  onChange: (option: ClientSortOption) => void;
}

/**
 * Reads the persisted sort preference from sessionStorage.
 * Returns the stored value if valid, otherwise returns the default.
 */
export function getPersistedSortOption(): ClientSortOption {
  const stored = safeStorageGet(
    typeof window !== 'undefined' ? window.sessionStorage : null,
    SORT_STORAGE_KEY,
  );
  if (stored && VALID_SORT_VALUES.has(stored)) {
    return stored as ClientSortOption;
  }
  return DEFAULT_SORT;
}

/**
 * SortControls renders a filter chip dropdown for choosing the project sort order.
 *
 * Uses the shared DashboardFilterChip component to match the visual style
 * of other dashboard section filters (h-9, Material Symbols icons, same colors).
 *
 * - Displays all 7 sort options with the currently selected one highlighted
 * - Persists selection to sessionStorage
 */
export function SortControls({ value, onChange }: SortControlsProps) {
  function handleChange(newValue: string) {
    const sortValue = newValue as ClientSortOption;
    onChange(sortValue);
    safeStorageSet(
      typeof window !== 'undefined' ? window.sessionStorage : null,
      SORT_STORAGE_KEY,
      sortValue,
    );
  }

  return (
    <DashboardFilterChip
      label="Sort"
      value={value}
      options={SORT_OPTIONS}
      onChange={handleChange}
    />
  );
}
